/**
 * UI Auditoria de Configuracion — catalogo de perfiles (ADR_012 DFCMGMT-05).
 *
 * Esta es la nueva vista raiz. Antes (ADR_011) mostraba devices sueltos;
 * ahora muestra perfiles de gestion. Click en un perfil navega al
 * drilldown (/admin/configuracion/{id}, PR-CMgmt-E componente
 * ConfigProfileDetailPage).
 *
 * Boton "+ Nuevo perfil" arriba a la derecha lleva al wizard
 * (/admin/configuracion/nuevo).
 */
import { Loader2, Plus } from "lucide-react";
import { describeCron } from "@/components/ScheduleBuilder";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import type { ConfigurationProfileSummary } from "@/api/types";
import { useConfigurationProfiles } from "@/hooks/useConfigProfiles";
import { cn } from "@/lib/utils";

function formatRelative(iso: string | null | undefined): string {
  if (!iso) return "nunca";
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return "hace segundos";
  if (seconds < 3600) return `hace ${Math.floor(seconds / 60)} min`;
  if (seconds < 86_400) return `hace ${Math.floor(seconds / 3600)} h`;
  return `hace ${Math.floor(seconds / 86_400)} d`;
}

function CronLabel({ cron }: { cron: string }) {
  // Cron en lenguaje legible (p.ej. "0 6 * * 1" -> "Lunes a las 06:00").
  // El cron crudo queda en el title para auditoría.
  return (
    <span title={cron} className="text-xs text-muted-foreground">
      {describeCron(cron)}
    </span>
  );
}

export function AdminConfigPage() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string>("");

  const profiles = useConfigurationProfiles({
    status: statusFilter || undefined,
    per_page: 50,
  });

  const items = profiles.data?.items ?? [];

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">
          Auditoría de configuración
        </h1>
        <p className="text-sm text-muted-foreground">
          Perfiles activos. Cada perfil agrupa equipos del mismo tipo, los
          audita en el horario que definiste y compara contra la última versión
          funcional.
        </p>
      </header>

      <div className="flex items-center gap-2">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          aria-label="Filtrar por estado"
          className="rounded-md border bg-background px-3 py-1.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="">Todos los estados</option>
          <option value="active">Activo</option>
          <option value="paused">Pausado</option>
        </select>
        <span className="flex-1" />
        <button
          type="button"
          onClick={() => navigate("/admin/configuracion/nuevo")}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
        >
          <Plus className="size-4" />
          Nuevo perfil
        </button>
      </div>

      {profiles.isPending && !profiles.data ? (
        <div className="flex items-center justify-center rounded-md border bg-card p-10">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : items.length === 0 ? (
        <EmptyState />
      ) : (
        <ProfilesTable
          items={items}
          onSelect={(p) => navigate(`/admin/configuracion/${p.id}`)}
        />
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="rounded-md border bg-card p-10 text-center">
      <p className="text-sm font-medium">Sin perfiles configurados</p>
      <p className="mt-1 text-xs text-muted-foreground">
        Crea uno con el botón "+ Nuevo perfil" arriba para empezar a auditar
        configuraciones.
      </p>
    </div>
  );
}

function ProfilesTable({
  items,
  onSelect,
}: {
  items: ConfigurationProfileSummary[];
  onSelect: (p: ConfigurationProfileSummary) => void;
}) {
  return (
    <div className="overflow-hidden rounded-md border bg-card">
      <div className="grid grid-cols-12 gap-3 border-b bg-muted/30 px-4 py-2 text-xs font-medium uppercase text-muted-foreground">
        <div className="col-span-4">Perfil</div>
        <div className="col-span-1 text-right">Equipos</div>
        <div className="col-span-2">Estado</div>
        <div className="col-span-2">Schedule</div>
        <div className="col-span-2">Última captura</div>
        <div className="col-span-1 text-right">Cambios</div>
      </div>
      {items.map((p) => (
        <ProfileRow key={p.id} profile={p} onClick={() => onSelect(p)} />
      ))}
    </div>
  );
}

function ProfileRow({
  profile,
  onClick,
}: {
  profile: ConfigurationProfileSummary;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="grid w-full grid-cols-12 items-center gap-3 border-b px-4 py-3 text-left text-sm transition-colors hover:bg-muted/30"
    >
      <div className="col-span-4 min-w-0">
        <div className="truncate font-medium">{profile.name}</div>
        <div className="truncate text-xs text-muted-foreground">
          {profile.device_type} · {profile.collection_method}
        </div>
      </div>
      <div className="col-span-1 text-right text-sm">
        {profile.devices_count}
      </div>
      <div className="col-span-2">
        <StatusBadge status={profile.status} />
      </div>
      <div className="col-span-2">
        <CronLabel cron={profile.schedule_cron} />
      </div>
      <div className="col-span-2 text-xs text-muted-foreground">
        {formatRelative(profile.last_capture_at)}
      </div>
      <div className="col-span-1 text-right">
        {profile.devices_with_unreviewed_changes > 0 ? (
          <span className="rounded-md bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-400">
            {profile.devices_with_unreviewed_changes}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </div>
    </button>
  );
}

function StatusBadge({ status }: { status: string }) {
  const cls =
    status === "active"
      ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
      : status === "paused"
        ? "bg-amber-500/15 text-amber-700 dark:text-amber-400"
        : "bg-muted text-muted-foreground";
  const label =
    status === "active" ? "Activo" : status === "paused" ? "Pausado" : status;
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium",
        cls,
      )}
    >
      ● {label}
    </span>
  );
}
